"use client";

import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { toast } from "@/src/hooks/use-toast";
import { useApproveTaskMutation, useLocalTaskUpdateMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { type Task, TaskStatus } from "@/src/models/task";

export function TprOpsContinueButton({
  task,
  successTitle,
  successDescription,
  errorTitle = "Unable to continue the workflow",
}: {
  task: Task;
  successTitle: string;
  successDescription: string;
  errorTitle?: string;
}) {
  const approvalMutation = useApproveTaskMutation();
  const localTaskUpdateMutation = useLocalTaskUpdateMutation({ invalidateOnSettled: false });
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");

  const handleContinue = async () => {
    try {
      await approvalMutation.mutateAsync({ taskId: task.id, approved: true });
      localTaskUpdateMutation.mutate({ ...task, status: TaskStatus.PROCESSING });
      refreshTprWorkflowProgress(invalidateInstructions);
      toast({
        title: successTitle,
        description: successDescription,
      });
    } catch (error) {
      toast({
        title: errorTitle,
        description: error instanceof Error ? error.message : "Refresh the task and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    // `sm` to match every other continuation control on the referral timeline — this was the one
    // button on the page still rendering at the default size.
    <Button
      size="sm"
      onClick={handleContinue}
      disabled={approvalMutation.isPending || localTaskUpdateMutation.isPending}
    >
      {approvalMutation.isPending ? "Continuing..." : "Continue"}
    </Button>
  );
}
